/**
 * What the scheduler is still holding for one event (D158).
 *
 * @remarks
 * Two readers wanted the same filter and only one had it: `resolvePacing`
 * measures the distance to a countdown's soonest occurrence, and the
 * `event_pending` condition asks whether there is one at all. Stated once
 * here so a clock and a lock can never disagree about whether the thing is
 * still coming.
 *
 * A read, never a write — `scheduler-tick.ts` owns the queue, `apply-effect`
 * owns arming and canceling.
 *
 * @packageDocumentation
 */

import type { EventDefinitionId, RuntimeState } from '@orpheus/content-schema';

/** One occurrence still waiting on the scheduler. */
type PendingOccurrence = RuntimeState['scheduler']['pendingEvents'][number];

/**
 * Every occurrence of the named event that has not fired yet.
 *
 * @remarks
 * An event may have SEVERAL pending occurrences — a self-scheduling chain
 * re-arms before its predecessor is processed — so this returns them all and
 * lets each caller ask its own question of the set. `cancel_event` drops
 * every one of them, which is why "pending" and "the chain is running" are
 * the same fact.
 *
 * @param state - The live runtime state
 * @param eventId - The event to ask about
 * @returns Its pending occurrences, in scheduler order
 */
export const pendingOccurrencesOf = (state: RuntimeState, eventId: EventDefinitionId): readonly PendingOccurrence[] =>
	state.scheduler.pendingEvents.filter((pending) => pending.eventDefinitionId === eventId);
